import React from 'react'
import useSWRImmutable from 'swr/immutable'

import { getAllApplicantSkills, getCompany, getPositionContracts, getPositionInterview } from '../../../../api/companies'
import Loading from '../../../components/layout/Loading'
import type { CompanyPosition } from '../../../../api/companies/types'
import Collapsable from '../../../components/layout/Collapsable'
import ContractCard from '../../../components/companies/ContractCard'
import { useI18N } from '../../../../libraries/i18n/hook'
import InterviewCard from '../../../components/companies/InterviewCard'
import DescriptionText from '../../../components/content/DescriptionText'

type Props = { companyId: number, divisionId: number, subDivisionId: number, position: CompanyPosition }
const PositionInfo:React.FC<Props> = ({ companyId, divisionId, subDivisionId, position }) => {
  const { t } = useI18N()
  const { data: contractsData, isLoading: isLoadingContracts } = useSWRImmutable(['company', companyId, 'divisions', divisionId, 'sub-divisions', subDivisionId, 'positions', position.id, 'contracts'], () => getPositionContracts(companyId, divisionId, subDivisionId, position.id))
  const { data: interviewData, isLoading: isLoadingInterviews } = useSWRImmutable(['company', companyId, 'divisions', divisionId, 'sub-divisions', subDivisionId, 'positions', position.id, 'interviews'], () => getPositionInterview(companyId, divisionId, subDivisionId, position.id))
  const { data: applicantSkillsData, isLoading: isLoadingApplicantSkills } = useSWRImmutable(['interview', 'applicant-skills'], () => getAllApplicantSkills(companyId))
  const { data: companyData, isLoading: isLoadingCompany } = useSWRImmutable(['company', companyId], () => getCompany(companyId))

  if(isLoadingContracts || isLoadingInterviews || isLoadingApplicantSkills || isLoadingCompany) {
    return <Loading />
  }

  return (
    <div>
      <div className='px-4 mt-8 sm:grid sm:grid-cols-2 sm:gap-16'>
        <DescriptionText>{position.description}</DescriptionText>
        <div className='grid grid-cols-2 sm:flex gap-x-4'>
          {position.info && position.info.map((info) => (
            <div key={`${info.title}-${info.description}`} className='my-4 sm:my-0 flex-1'>
              <KeyInfoBox title={info.title} description={info.description} />
            </div>
          ))}
        </div>
      </div>
      <div className='mt-6'>
        {contractsData && contractsData.length > 0 && (
          <Collapsable label={<h3 className='text-black'>{t('companies.position.details', undefined, companyData?.languageCode)}</h3>}>
            <ul>
              {contractsData.map((contract) => (
                <li key={contract.id}>
                  <ContractCard contract={contract} languageCode={companyData?.languageCode} />
                </li>
              ))}
            </ul>
          </Collapsable>
        )}
        {interviewData && applicantSkillsData && (
          <Collapsable label={<h3 className='text-black'>{t('companies.process.selection', undefined, companyData?.languageCode)}</h3>}>
            <InterviewCard interview={interviewData} allSkills={applicantSkillsData} languageCode={companyData?.languageCode} />
          </Collapsable>
        )}
      </div>
    </div>
  )
}

const KeyInfoBox = ({ title, description }: { title: string, description: string }) => (
  <div className='shadow bg-soft-green rounded-md p-4 text-center text-black'>
    <b className='text-xl sm:text-3xl font-semibold'>{title}</b>
    <p className='text-xs sm:text-sm'>{description}</p>
  </div>
)

export default PositionInfo
